const events = require( 'eventslibjs' );

import {
	AJAXRequest,
	isEmpty,
	isDefined,
	triggerEvent
} from './Utilities';

class FrontendLogin {
	constructor(){
		// Get elements
		this.getElements();

		// Check if we have to invoke the methods
		if ( this.shouldInvokeMethods() ){
			// Bind form submission
			this.validateFormSubmission();

			// Handle reCAPTCHA changes
			this.handleReCaptchaChanges();
		}
	}

	getElements(){
		// Get the main elements
		this.$elements = {}

		// Define the main CSS classes
		this.cssClasses = {}
	}

	get formData(){
		return {}
	}

	get ajaxAction(){
		return '';
	}

	validateFormSubmission(){
		// Check if the form exists
		if ( isDefined( this.$elements.mainForm ) ){
			// Bind submission
			this.$elements.mainForm.onsubmit = ( event ) => {
				// Create a variable to evaluate if the form has errors later
				let canSubmit = true;

				// Check if the recaptcha is enabled, but the checkbox is not checked
				if ( this.hasReCAPTCHA() && ! this.isCaptchaChecked() ){
					// Add a CSS class for the reCAPTCHA error
					this.$elements.container.classList.add( this.cssClasses.reCAPTCHAerror );

					// Update the error status
					canSubmit = false;

					// Set frontend error
					this.setError( UncannyToolkit.frontendLogin.i18n.checkReCaptcha );

					console.error( '✋Frontend Login: The form is using reCAPTCHA, but you didn\'t pass the verification' );
				}

				// Trigger custom event
				triggerEvent( `uncanny-toolkit/frontend-login/${ this.id }/validated`, {
					isValid: canSubmit
				});

				// Check if we can submit the form
				if ( canSubmit ){
					// Submit the form
					this.submitForm( event );
				}
				else {
					// Prevent the form submission
					event.preventDefault();

					console.error( '✋Frontend Login: Submission prevented' );
				}
			};
		}
	}

	submitForm( event ){
		// Hide errors
		this.setError( false );

		// Add the loading animation
		this.setLoadingStatus( true );

		// Disable the submit button
		this.setButtonDisableStatus( true );

		/**
		 * Handles the AJAX submission
		 * 
		 * @return {undefined}
		 */
		const handleAJAX = () => {
			// Trigger custom event
			triggerEvent( `uncanny-toolkit/frontend-login/${ this.id }/submitting` );

			// Do AJAX call
			AJAXRequest( this.ajaxAction, this.formData, ( response ) => {
				// Remove the loading animation
				this.setLoadingStatus( false );

				// Enable the submit button
				this.setButtonDisableStatus( false );

				// Check if the call was really successful
				if ( response.success ){
					// Trigger custom event
					triggerEvent( `uncanny-toolkit/frontend-login/${ this.id }/submitted`, {
						success: true
					});

					this.afterValidSubmission( response );
				}
				else {
					// Trigger custom event
					triggerEvent( `uncanny-toolkit/frontend-login/${ this.id }/submitted`, {
						success: false,
						message: isDefined( response.message ) ? response.message : UncannyToolkit.i18n.error.generic
					});

					// Check if there is a message
					if ( isDefined( response.message ) ){
						// Show the error
						this.setError( response.message );
					}
					else {
						// Set a generic error message
						this.setError( UncannyToolkit.i18n.error.generic );
					}

					// Reset reCAPTCHA
					this.resetReCAPTCHA();
				}
			}, ( response ) => {
				// Check if we have the error code and the code is 302
				if ( isDefined( response.code ) && response.code == 302 ) {
					// Check if "Prevent Concurrent Login" is active
					if ( UncannyToolkit.integrity.shouldPreventConcurrentLogin ) {
						// Set custom LD message
						response.message = UncannyToolkit.i18n.preventConcurrentLogin;
					}
				}

				// Trigger custom event
				triggerEvent( `uncanny-toolkit/frontend-login/${ this.id }/submitted`, {
					success: false,
					message: isDefined( response.message ) ? response.message : UncannyToolkit.i18n.error.generic
				});
					
				// Check if there is a message
				if ( isDefined( response.message ) ){
					// Show the error
					this.setError( response.message );
				}
				else {
					// Set a generic error message
					this.setError( UncannyToolkit.i18n.error.generic );
				}

				// Reset reCAPTCHA
				this.resetReCAPTCHA();

				// Remove the loading animation
				this.setLoadingStatus( false );

				// Enable the submit button
				this.setButtonDisableStatus( false );
			}, {
				includeNonce: false
			});
		}

		// If it's not using the LD reCAPTCHA
		if ( ! this.isUsingLearnDashReCaptcha() ) {
			// Check if it's using AJAX
			if ( UncannyToolkit.frontendLogin.hasAjaxEnabled ){
				// Prevent the form submission
				event.preventDefault();

				// Handle AJAX
				handleAJAX();
			} else {
				// Do nothing, don't prevent the event
			}
		} else {
			// If it's using LD's reCAPTCHA:

			// Check if it's using AJAX
			if ( UncannyToolkit.frontendLogin.hasAjaxEnabled ) {
				// Prevent the form submission
				event.preventDefault();

				// Handle reCAPTCHA
				// The callback will be invoked once the reCAPTCHA is validated
				this.handleLearnDashReCaptcha( () => {
					// Handle AJAX
					handleAJAX();
				} );
			} else {
				// Check if it has the required data already
				// We're doing this because for non-AJAX forms this is a recursive function
				// Once validated, it will invoke itself. We have to prevent an infinito loop,
				// so after the data is validated, we add a hidden field to the form.
				// If the field exists, it means this function was already executed, and that we
				// don't have to prevent the submission again. (Only for non-AJAX forms)
				if ( ! this.hasLearnDashReCaptchField() ) {
					// Prevent the form submission even if it's not using AJAX
					event.preventDefault();

					// Handle reCAPTCHA
					// The callback will be invoked once the reCAPTCHA is validated
					this.handleLearnDashReCaptcha( () => {
						// Submit the form again
						this.$elements.mainForm.submit();
					} );
				}
			}
		}
	}

	afterValidSubmission( response ){}

	setLoadingStatus( setAsLoading = true ){
		// Check if we have to add a loading animation to the submit button
		if ( UncannyToolkit.frontendLogin.ui.showAnimationOnSubmit ){
			// Check if we have to enable the loading status
			if ( setAsLoading ){
				// Add the loading animation
				this.$elements.container.classList.add( this.cssClasses.loading );
				this.$elements.submitButton.container.classList.add( this.cssClasses.btnLoading );

				// Trigger custom event
				triggerEvent( `uncanny-toolkit/frontend-login/${ this.id }/set-loading`, {
					isLoading: true
				});
			}
			else {
				// Remove the loading status
				this.$elements.container.classList.remove( this.cssClasses.loading );
				this.$elements.submitButton.container.classList.remove( this.cssClasses.btnLoading );

				// Trigger custom event
				triggerEvent( `uncanny-toolkit/frontend-login/${ this.id }/set-loading`, {
					isLoading: false
				});
			}
		}
	}

	setButtonDisableStatus( setAsDisabled = true ){
		// Check if we have to disable the submit button
		if ( UncannyToolkit.frontendLogin.ui.buttonDisabledOnSubmit ){
			// Check if we have to set it as disabled
			if ( setAsDisabled ){
				// Disable the submit button
				this.$elements.container.classList.add( this.cssClasses.disabled );
				this.$elements.submitButton.container.classList.add( this.cssClasses.btnDisabled );
				this.$elements.submitButton.button.setAttribute( 'disabled', true );

				// Trigger custom event
				triggerEvent( `uncanny-toolkit/frontend-login/${ this.id }/disable-button`, {
					disable: true
				});
			}
			else {
				// Disable the submit button
				this.$elements.container.classList.remove( this.cssClasses.disabled );
				this.$elements.submitButton.container.classList.remove( this.cssClasses.btnDisabled );
				this.$elements.submitButton.button.removeAttribute( 'disabled' );

				// Trigger custom event
				triggerEvent( `uncanny-toolkit/frontend-login/${ this.id }/disable-button`, {
					disable: false
				});
			}
		}
	}

	setError( error = false ){
		// Check if the error is defined
		if ( ! isEmpty( error ) ){
			// Set the class to show the notice
			this.$elements.errorNotice.container.classList.add( this.cssClasses.noticeHasError );

			// Set the error message
			this.$elements.errorNotice.textNode.innerHTML = error;
		}
		else {
			// Set the class to hide the notice
			this.$elements.errorNotice.container.classList.remove( this.cssClasses.noticeHasError );

			// Set the error message
			this.$elements.errorNotice.textNode.textContent = '';

			// Remove a CSS class for the reCAPTCHA error
			this.$elements.container.classList.remove( this.cssClasses.reCAPTCHAerror );
		}
	}

	handleReCaptchaChanges(){
		// Add global method to handle what happened when
		// the reCAPTCHA is checked
		UncannyToolkit.frontendLogin.reCaptcha = {
			correct: ( response ) => {
				// Reset errors
				this.setError( false );
			}
		}
	}

	hasReCAPTCHA(){
		return isDefined( this.$elements.formFields.reCAPTCHA );
	}

	getReCAPTCHAResponse(){
		// Get the reCAPTCHA ID
		const id = this.$elements.formFields.reCAPTCHA.getAttribute( 'data-id' );

		// Get the response
		return grecaptcha.getResponse( id );
	}

	resetReCAPTCHA(){
		try {
			// Get the reCAPTCHA ID
			const id = this.$elements.formFields.reCAPTCHA.getAttribute( 'data-id' );

			// Reset the reCAPTCHA
			grecaptcha.reset( id );
		} catch ( e ){}
	}

	isOnModal(){
		return isDefined( UncannyToolkit.modals[ 'ult-login' ] );
	}

    isCaptchaChecked(){
        return ! isEmpty( this.getReCAPTCHAResponse() );
    }

    shouldInvokeMethods(){
    	return isDefined( this.$elements.container );
    }

    handleLearnDashReCaptcha( callback ) {
    	// Check if it's using LD's reCAPTCHA
    	if ( this.isUsingLearnDashReCaptcha() ) {
    		// Invoke the challenge
    		grecaptcha.ready( () => {
			    grecaptcha.execute(
			    	LD_TP.site_key, 
			    	{ action: 'submit' }
			    ).then( ( token ) => {
			    	// Add hidden field to the form
			    	if ( isDefined( this.$elements.mainForm ) ) {
			    		// Add hidden field with token
			    		this.$elements.mainForm.insertAdjacentHTML( 'afterbegin', `<input type="hidden" id="ult-form-ld-recaptcha" name="token" value="${ token }">` );
			    	}

			    	// Invoke callback
			    	if ( isDefined( callback ) ) {
			    		callback();
			    	}
			    });
			});
    	} else {
    		// Just invoke the callback
	    	if ( isDefined( callback ) ) {
	    		callback();
	    	}
    	}
    }

    isUsingLearnDashReCaptcha() {
    	// Check if grecaptcha is defined
    	// Check if LD_TP is defined
    	// Check if LD_TP.site_key is defined
    	return isDefined( window.grecaptcha ) && isDefined( window.LD_TP ) && isDefined( LD_TP.site_key );
    }

    hasLearnDashReCaptchField() {
    	let hasField = false;

    	// Check if the element exists
    	if ( isDefined( this.$elements.mainForm ) ) {
    		// Check if the field exists
    		hasField = isDefined( this.$elements.mainForm.querySelector( 'input[name="token"]' ) );
    	}

    	return hasField;
    }
}

export class Login extends FrontendLogin {
	getElements(){
		// Get the main elements
		this.$elements = {
			container: document.getElementById( 'ult-login' ),
			mainForm:  document.getElementById( 'ult-login-form' ),
			submitButton: {
				container: document.getElementsByClassName( 'login-submit' )[0],
				button: document.getElementById( 'ult-login-submit' )
			},
			errorNotice: {
				container: document.querySelector( '#ult-login .ult-form__validation' ),
				textNode: document.querySelector( '#ult-login .ult-notice-text' ),
			},
			formFields: {
				email: document.getElementById( 'ult-login-email' ),
				password: document.getElementById( 'ult-login-password' ),
				redirectTo: document.querySelector( '#ult-login .login-submit input[name="redirect_to"]' ),
				reCAPTCHA: document.querySelector( '#ult-login .ult-form-recaptcha' ),
				rememberMe: document.getElementById( 'ult-login-remember' ),
			}
		}
	}

	get id(){
		return 'login';
	}

	// Define the AJAX action ID
	get ajaxAction(){
		return 'ult-login';
	}

	get cssClasses(){
		return {
			reCAPTCHAerror: 'ult-login--recaptcha-error',
			loading:        'ult-login--loading',
			disabled:       'ult-login--disabled',
			btnLoading:     'ult-form__submit-btn--loading',
			btnDisabled:    'ult-form__submit-btn--disabled',
			noticeHasError: 'ult-form__validation--has-error' 
		};
	}

	get formData(){
		// Get the form data
		const formData = {
			email: this.$elements.formFields.email.value,
			password: this.$elements.formFields.password.value,
			redirectTo: this.$elements.formFields.redirectTo.value,
		}

		// Check if it has the "Remember me" checkbox
		if ( isDefined( this.$elements.formFields.rememberMe ) ){
			formData.rememberMe = this.$elements.formFields.rememberMe.checked;
		}

		// Check if it has a reCAPTCHA
		if ( this.hasReCAPTCHA() ){
			formData[ 'g-recaptcha-response' ] = this.getReCAPTCHAResponse();
		}

		// Check if it has a LearnDash reCAPTCHA
		if ( isDefined( this.$elements.mainForm.querySelector( '#ult-form-ld-recaptcha' ) ) ) {
			formData.token = this.$elements.mainForm.querySelector( '#ult-form-ld-recaptcha' ).value;
		}

		return formData;
	}

	afterValidSubmission( response ){

		// Show the loading animation again, we're going 
		// to redirect them to another page and it might
		// take some seconds

		// Add the loading animation
		this.setLoadingStatus( true );

		// Disable the submit button
		this.setButtonDisableStatus( true );

		// Check if we have to redirect to a URL
		if ( ! isEmpty( response.redirectTo ) ){
			window.location.href = response.redirectTo;
		}
		// Otherwise, just reload the page
		else {
			window.location.reload();
		}
	}
}

export class ForgotPassword extends FrontendLogin {
	getElements(){
		// Get the main elements
		this.$elements = {
			container: document.getElementById( 'ult-forgot-password' ),
			boxContent: document.querySelector( '#ult-forgot-password .ult-form__content' ),
			mainForm:  document.getElementById( 'ult-forgot-password-form' ),
			submitButton: {
				container: document.querySelector( '.ult-form__row--submit' ),
				button: document.getElementById( 'ult-forgot-password-submit-btn' )
			},
			errorNotice: {
				container: document.querySelector( '#ult-forgot-password .ult-form__validation' ),
				textNode: document.querySelector( '#ult-forgot-password .ult-notice-text' ),
			},
			formFields: {
				email: document.getElementById( 'ult-forgot-email' ),
				reCAPTCHA: document.querySelector( '#ult-forgot-password .ult-form-recaptcha' ),
			}
		}
	}

	get id(){
		return 'forgot-password';
	}

	// Define the AJAX action ID
	get ajaxAction(){
		return 'ult-forgot-password';
	}

	get cssClasses(){
		return {
			reCAPTCHAerror: 'ult-forgot-password--recaptcha-error',
			loading:        'ult-forgot-password--loading',
			disabled:       'ult-forgot-password--disabled',
			btnLoading:     'ult-form__submit-btn--loading',
			btnDisabled:    'ult-form__submit-btn--disabled',
			noticeHasError: 'ult-form__validation--has-error' 
		};
	}

	get formData(){
		// Get the form data
		const formData = {
			email: this.$elements.formFields.email.value
		}

		// Check if it has a reCAPTCHA
		if ( this.hasReCAPTCHA() ){
			formData[ 'g-recaptcha-response' ] = this.getReCAPTCHAResponse();
		}

		// Check if it has a LearnDash reCAPTCHA
		if ( isDefined( this.$elements.mainForm.querySelector( '#ult-form-ld-recaptcha' ) ) ) {
			formData.token = this.$elements.mainForm.querySelector( '#ult-form-ld-recaptcha' ).value;
		}

		return formData;
	}

	afterValidSubmission( response ){
		// Show a confirmation message
		this.$elements.boxContent.innerHTML = response.message;
	}
}

export class ResetPassword extends FrontendLogin {
	getElements(){
		// Get the main elements
		this.$elements = {
			container: document.getElementById( 'ult-reset-password' ),
			boxContent: document.querySelector( '#ult-reset-password .ult-form__content' ),
			mainForm:  document.getElementById( 'ult-reset-password-form' ),
			submitButton: {
				container: document.querySelector( '.ult-form__row--submit' ),
				button: document.getElementById( 'ult-reset-password-submit-btn' )
			},
			errorNotice: {
				container: document.querySelector( '#ult-reset-password .ult-form__validation' ),
				textNode: document.querySelector( '#ult-reset-password .ult-notice-text' ),
			},
			formFields: {
				password: document.getElementById( 'ult-reset-new-password' ),
				passwordRepeat: document.getElementById( 'ult-reset-confirm-password' ),
				reCAPTCHA: document.querySelector( '#ult-reset-password .ult-form-recaptcha' ),
			}
		}
	}

	get id(){
		return 'reset-password';
	}

	// Define the AJAX action ID
	get ajaxAction(){
		return 'ult-reset-password';
	}

	get cssClasses(){
		return {
			reCAPTCHAerror: 'ult-reset-password--recaptcha-error',
			loading:        'ult-reset-password--loading',
			disabled:       'ult-reset-password--disabled',
			btnLoading:     'ult-form__submit-btn--loading',
			btnDisabled:    'ult-form__submit-btn--disabled',
			noticeHasError: 'ult-form__validation--has-error' 
		};
	}

	get formData(){
		// Get the form data
		const formData = {
			password: this.$elements.formFields.password.value,
			passwordRepeat: this.$elements.formFields.passwordRepeat.value,
		}

		// Check if it has a reCAPTCHA
		if ( this.hasReCAPTCHA() ){
			formData[ 'g-recaptcha-response' ] = this.getReCAPTCHAResponse();
		}

		// Check if it has a LearnDash reCAPTCHA
		if ( isDefined( this.$elements.mainForm.querySelector( '#ult-form-ld-recaptcha' ) ) ) {
			formData.token = this.$elements.mainForm.querySelector( '#ult-form-ld-recaptcha' ).value;
		}

		return formData;
	}

	afterValidSubmission( response ){
		// Show a confirmation message
		this.$elements.boxContent.innerHTML = response.message;
	}
}

/**
 * This class handles the login functionality when 
 */
export class FrontendLogin_Modal {
	constructor(){
		// Check if we should invoke the callbacks
		if ( this.shouldInvokeMethods() ){
			// Get elements
			this.getElements();

			// Handle clicks to the buttons to show the sections
			this.handleContainerVisiblity();
		}
		else {
			// Check if the current page is the Login page
			// That would mean that the modal is not defined here
			// We want to reload the page when they click on the "Login" link
			if ( 
				isDefined( UncannyToolkit.frontendLogin ) && 
				isDefined( UncannyToolkit.frontendLogin.currentPageIsLoginPage ) &&  
				UncannyToolkit.frontendLogin.currentPageIsLoginPage
			){
				this.handleModalInLoginPage();
			}
		}
	}

	getElements(){
		this.$elements = {
			container: document.getElementById( 'ult-login-modal' ),
			loginShowButton: document.getElementById( 'ult-form-footer-login' ),
			forgotPasswordShowButton: document.getElementById( 'ult-form-footer-forgot-password' ),
		}
	}

	handleContainerVisiblity(){
		if ( isDefined( this.$elements.forgotPasswordShowButton ) ){
			// Listen clicks to the button to show the Forgot Password section
			this.$elements.forgotPasswordShowButton.addEventListener( 'click', ( event ) => {
				// Prevent the event, we don't want to send them to another page
				event.preventDefault();

				// Show the Forgot Password form
				this.showSection( 'forgot-password' );
			});
		}
		
		if ( isDefined( this.$elements.loginShowButton ) ){
			// Listen clicks to the button to show the Log in section
			this.$elements.loginShowButton.addEventListener( 'click', ( event ) => {
				// Prevent the event, we don't want to send them to another page
				event.preventDefault();

				// Show the Forgot Password form
				this.showSection( 'login' );
			});
		}
	}

	showSection( section = 'login' ){
		// Remove both classes. We'll add the correct one later
		this.$elements.container.classList.remove( 'ult-login-modal--login', 'ult-login-modal--forgot-password' );

		// Show and hide sections
		if ( section == 'login' ){
			this.$elements.container.classList.add( 'ult-login-modal--login' );
		}
		else if ( section == 'forgot-password' ){
			this.$elements.container.classList.add( 'ult-login-modal--forgot-password' );
		}
	}

	shouldInvokeMethods(){
		return (
			isDefined( UncannyToolkit.frontendLogin ) && 
			isDefined( UncannyToolkit.frontendLogin.currentPageIsLoginPage ) && 
			! UncannyToolkit.frontendLogin.currentPageIsLoginPage && 
			isDefined( UncannyToolkit.modals[ 'ult-login' ] )
		);
	}

	handleModalInLoginPage(){
		events.on( 'click', `[href*="ult-modal-open----ult-login"]`, ( event ) => {
			// Prevent default
			event.preventDefault();

			// Reload the page
			location.reload();
		});
	}
}